import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from './axiosConfig';
import errorHandler from './utils/errorHandler';
import {
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Paper,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import salonLogo from './Salon Logic S.jpg';

const PasswordReset = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Password validation
  const passwordRequirements = [
    { label: 'At least 8 characters', test: (pwd) => pwd.length >= 8 },
    { label: 'Contains uppercase letter', test: (pwd) => /[A-Z]/.test(pwd) },
    { label: 'Contains lowercase letter', test: (pwd) => /[a-z]/.test(pwd) },
    { label: 'Contains number', test: (pwd) => /[0-9]/.test(pwd) },
    { label: 'Passwords match', test: (pwd) => pwd === confirmPassword && pwd.length > 0 }
  ];

  useEffect(() => {
    const verifyToken = async () => {
      try {
        errorHandler.logDebug('PasswordReset', 'Verifying reset token');
        await api.get(`/auth/verify_reset_token/${token}`);
        setIsValid(true);
        setIsLoading(false);
        errorHandler.logDebug('PasswordReset', 'Token verified successfully');
      } catch (error) {
        errorHandler.logError('PasswordReset', 'Token verification failed', error);
        setError('This password reset link is invalid or has expired.');
        setIsLoading(false);
        setTimeout(() => navigate('/password-recovery'), 3000);
      }
    };
    verifyToken();
  }, [token, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validate password requirements
    const allRequirementsMet = passwordRequirements.every(req => req.test(password));
    if (!allRequirementsMet) {
      setError('Please ensure all password requirements are met.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      errorHandler.logDebug('PasswordReset', 'Submitting password reset');
      await api.post('/auth/reset_password', { token, password });
      
      setSuccess(true);
      setMessage('Password reset successfully! Redirecting to login...');
      errorHandler.logDebug('PasswordReset', 'Password reset successful');
      
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      errorHandler.logError('PasswordReset', 'Password reset failed', error);
      setError(error.response?.data?.error || 'An error occurred while resetting your password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            Verifying reset link...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Invalid token state
  if (!isValid) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Card elevation={4}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CancelIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Invalid Reset Link
              </Typography>
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
              <Button
                component={Link}
                to="/password-recovery"
                variant="contained"
                sx={{ mt: 3 }}
              >
                Request New Link
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  // Success state
  if (success) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Card elevation={4}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Password Reset Successful!
              </Typography>
              <Alert severity="success" sx={{ mt: 2 }}>
                {message}
              </Alert>
              <LinearProgress sx={{ mt: 3 }} />
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  // Main reset form
  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        {/* Logo and Title */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src={salonLogo}
            alt="Salon Logic Pro Logo"
            sx={{ 
              width: 80, 
              height: 80, 
              mb: 2,
              objectFit: 'contain'
            }}
          />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Set New Password
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Choose a strong password for your account
          </Typography>
        </Box>

        {/* Main Card */}
        <Card elevation={4}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <LockIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Create New Password
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isSubmitting}
                autoComplete="new-password"
                autoFocus
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isSubmitting}
                autoComplete="new-password"
                sx={{ mb: 3 }}
              />

              {/* Password Requirements */}
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Password Requirements:
                </Typography>
                <List dense>
                  {passwordRequirements.map((req, index) => {
                    const isMet = req.test(password);
                    return (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {isMet ? (
                            <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: 18, color: 'grey.400' }} />
                          )}
                        </ListItemIcon>
                        <ListItemText 
                          primary={req.label}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: isMet ? 'success.main' : 'textSecondary'
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={isSubmitting || !passwordRequirements.every(req => req.test(password))}
                sx={{ py: 1.5 }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default PasswordReset;